import { useEffect } from "react";
import { MessageCircle, Minus, ArrowLeft } from "lucide-react";
import { ChatListPage } from "@/app/components/chat-list-page";
import { ChatPage } from "@/app/components/chat-page";
import type { Conversation } from "@/app/components/chat-list-page";
import type { ChatMessage } from "@/app/hooks/use-app-state";

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, show thread; when null, show list. */
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onBackToList: () => void;
  conversations: Conversation[];
  setConversations: (conversations: Conversation[] | ((prev: Conversation[]) => Conversation[])) => void;
  chatMessages: ChatMessage[];
  onSendMessage: (conversationId: string, message: string) => void;
  onViewOfferDetails?: (offerId: string, sender: "user" | "other") => void;
  userId: string;
  /** Shared nav/tab props for ChatListPage/ChatPage (not used for nav inside modal but required by components). */
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
}

export function ChatModal({
  isOpen,
  onClose,
  selectedConversationId,
  onSelectConversation,
  onBackToList,
  conversations,
  setConversations,
  chatMessages,
  onSendMessage,
  onViewOfferDetails,
  userId,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
}: ChatModalProps) {
  const conversation = conversations.find((c) => c.id === selectedConversationId);
  const conversationMessages = selectedConversationId
    ? chatMessages.filter((m) => m.conversationId === selectedConversationId)
    : [];

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
        aria-hidden
        onClick={onClose}
      />

      {/* Panel: bottom sheet on mobile (fully rounded), centered card on desktop */}
      <div
        className="fixed z-[101] flex flex-col bg-card border border-border shadow-2xl animate-in slide-in-from-bottom-8 duration-300 ease-out
          inset-x-0 bottom-2 top-[10%] rounded-3xl
          md:inset-auto md:left-1/2 md:top-1/2 md:bottom-auto md:right-auto md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-3xl md:w-full md:max-w-lg md:max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Mensagens"
      >
        {/* Modal header: minimize + optional back + title */}
        <div className="flex items-center gap-2 shrink-0 px-4 py-3 border-b border-border bg-card/95 backdrop-blur">
          {conversation ? (
            <button
              type="button"
              onClick={onBackToList}
              className="p-2 -ml-2 hover:bg-accent rounded-lg transition-colors"
              aria-label="Voltar para lista"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <div className="w-9" aria-hidden />
          )}
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary shrink-0" />
            <h2 className="text-lg font-semibold truncate">
              {conversation
                ? conversation.adTitle
                  ? `${conversation.name} - ${conversation.adTitle}`
                  : conversation.name
                : "Mensagens"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            aria-label="Minimizar"
          >
            <Minus className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {!conversation ? (
            <ChatListPage
              onBack={onClose}
              activeTab={activeTab}
              onTabChange={onTabChange}
              favoritesCount={favoritesCount}
              onMenuClick={onMenuClick}
              onPostAdClick={onPostAdClick}
              conversations={conversations}
              setConversations={setConversations}
              onSelectConversation={onSelectConversation}
              userId={userId}
              hideBottomNav
              hidePageHeader
            />
          ) : (
            <ChatPage
              onBack={onBackToList}
              conversationName={conversation.name}
              conversationType={conversation.type}
              adTitle={conversation.adTitle}
              otherPartyProfileImageUrl={conversation.avatar}
              conversationId={conversation.id}
              userId={userId}
              initialMessages={
                conversationMessages.length > 0 ? conversationMessages : undefined
              }
              onSendMessage={(msg) => onSendMessage(conversation.id, msg)}
              onViewOfferDetails={onViewOfferDetails}
              activeTab={activeTab}
              onTabChange={onTabChange}
              favoritesCount={favoritesCount}
              onMenuClick={onMenuClick}
              onPostAdClick={onPostAdClick}
              hideBottomNav
              hidePageHeader
              onThreadViewed={(id) =>
                setConversations((prev) =>
                  prev.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c))
                )
              }
            />
          )}
        </div>
      </div>
    </>
  );
}
